import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'components/ui/Button'
import { Input } from 'components/ui/Input'
import { Modal } from 'components/ui/Modal'
import { Badge } from 'components/ui/Badge'
import { 
  rolesApi,
  type Role, 
  type Permission,
  type CreateRoleData
} from 'lib/api'
import { useToast } from 'contexts/ToastContext'
import { 
  Shield, 
  Eye, 
  EyeOff, 
  CheckCircle,
  AlertCircle,
  Info
} from 'lucide-react'

interface CreateRoleModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  editingRole?: Role | null
}

interface FormData {
  name: string
  description: string
  color: string
  is_active: boolean
  selectedPermissions: number[]
}

const DEFAULT_COLORS = [
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Yellow
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#06B6D4', // Cyan
  '#84CC16', // Lime
  '#F97316', // Orange
  '#EC4899', // Pink
  '#6B7280', // Gray
]

export function CreateRoleModal({ isOpen, onClose, onSuccess, editingRole }: CreateRoleModalProps) {
  const { t } = useTranslation()
  const { addToast } = useToast()
  const [loading, setLoading] = useState(false)
  const [permissions, setPermissions] = useState<Permission[]>([])
  const [fetchingPermissions, setFetchingPermissions] = useState(false)
  const [nameError, setNameError] = useState<string>('')
  
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    color: DEFAULT_COLORS[0],
    is_active: true,
    selectedPermissions: []
  })

  const isEditMode = !!editingRole

  // Reset form when modal opens or editing role changes
  useEffect(() => {
    if (isOpen) {
      if (editingRole) {
        // Edit mode - populate form with role data
        setFormData({
          name: editingRole.name,
          description: editingRole.description,
          color: editingRole.color,
          is_active: editingRole.is_active,
          selectedPermissions: editingRole.permissions.map(p => p.id)
        })
        setNameError('')
      } else {
        // Create mode - reset form
        setFormData({
          name: '',
          description: '',
          color: DEFAULT_COLORS[0],
          is_active: true,
          selectedPermissions: []
        })
        setNameError('')
      }
      fetchPermissions()
    }
  }, [isOpen, editingRole])

  const fetchPermissions = async () => {
    try {
      setFetchingPermissions(true)
      let page = 1
      const all: Permission[] = []
      // Loop through paginated results
      // Stops when response has no next or returns empty array
      // Supports both array and paginated object structures
      while (true) {
        const response = await rolesApi.getPermissions(undefined, page)
        const data: any = response.data as any
        const pageItems: Permission[] = Array.isArray(data) ? data : (data?.results || [])
        all.push(...pageItems)
        const hasNext = !Array.isArray(data) && !!data?.next
        if (!hasNext || pageItems.length === 0) break
        page += 1
      }
      setPermissions(all)
    } catch (error) {
      console.error('Error fetching permissions:', error)
      addToast({
        type: 'error',
        title: t('common.error'),
        message: t('roles.messages.loadPermissionsError'),
        duration: 5000
      })
    } finally {
      setFetchingPermissions(false)
    }
  }

  const getPermissionsByCategory = (category: string) => {
    return permissions.filter(p => p.category === category)
  }

  const getPermissionCategories = () => {
    return [...new Set(permissions.map(p => p.category))]
  }

  const handlePermissionToggle = (permissionId: number) => {
    setFormData(prev => ({
      ...prev,
      selectedPermissions: prev.selectedPermissions.includes(permissionId)
        ? prev.selectedPermissions.filter(id => id !== permissionId)
        : [...prev.selectedPermissions, permissionId]
    }))
  }

  const handleCategoryToggle = (category: string, selectAll: boolean) => {
    const categoryPermissions = getPermissionsByCategory(category)
    const categoryPermissionIds = categoryPermissions.map(p => p.id)
    
    setFormData(prev => ({
      ...prev,
      selectedPermissions: selectAll
        ? [...new Set([...prev.selectedPermissions, ...categoryPermissionIds])]
        : prev.selectedPermissions.filter(id => !categoryPermissionIds.includes(id))
    }))
  }

  const validateName = (): string | null => {
    if (!formData.name.trim()) {
      return t('roles.validation.nameRequired')
    }
    if (formData.name.trim().length < 2) {
      return t('roles.validation.nameTooShort')
    }
    return null
  }

  const validateForm = (): string | null => {
    const nErr = validateName()
    if (nErr) return nErr
    if (formData.selectedPermissions.length === 0) {
      return t('roles.validation.permissionsRequired')
    }
    return null
  }

  const handleSubmit = async () => {
    const validationError = validateForm()
    if (validationError) {
      // Set inline name error when applicable
      const nErr = validateName()
      setNameError(nErr || '')
      addToast({
        type: 'error',
        title: t('common.error'),
        message: validationError,
        duration: 5000
      })
      return
    }
    setNameError('')

    setLoading(true)
    try {
      const roleData: CreateRoleData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        color: formData.color,
        is_active: formData.is_active,
        permission_ids: formData.selectedPermissions
      }

      if (isEditMode && editingRole) {
        await rolesApi.updateRole(editingRole.id, roleData)
        addToast({
          type: 'success',
          title: t('common.success'),
          message: t('roles.messages.roleUpdated'),
          duration: 3000
        })
      } else {
        await rolesApi.createRole(roleData)
        addToast({
          type: 'success',
          title: t('common.success'),
          message: t('roles.messages.roleCreated'),
          duration: 3000
        })
      }

      onSuccess()
      onClose()
    } catch (error: any) {
      console.error('Error saving role:', error)
      addToast({
        type: 'error',
        title: t('common.error'),
        message: error.response?.data?.error || t('roles.messages.saveError'),
        duration: 5000
      })
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (!loading && !fetchingPermissions) {
      onClose()
    }
  }

  const getSelectedCountForCategory = (category: string) => {
    const categoryPermissions = getPermissionsByCategory(category)
    return formData.selectedPermissions.filter(id => 
      categoryPermissions.some(p => p.id === id)
    ).length
  }

  const isAllSelectedForCategory = (category: string) => {
    const categoryPermissions = getPermissionsByCategory(category)
    return categoryPermissions.length > 0 && 
           categoryPermissions.every(p => formData.selectedPermissions.includes(p.id))
  }

  const isSomeSelectedForCategory = (category: string) => {
    const categoryPermissions = getPermissionsByCategory(category)
    return categoryPermissions.some(p => formData.selectedPermissions.includes(p.id))
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditMode ? t('roles.editRole') : t('roles.createRole')}
      className="max-w-4xl max-[1149px]:max-w-[95vw] max-[1149px]:max-h-[95vh]"
    >
      <div className="space-y-6 max-[1149px]:space-y-4">
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-[1149px]:gap-3">
          <div>
            <label className="text-sm font-medium text-foreground max-[1149px]:text-xs">
              {t('roles.roleName')} *
            </label>
            <Input
              value={formData.name}
            onChange={(e) => {
              const value = e.target.value
              setFormData(prev => ({ ...prev, name: value }))
              if (nameError) {
                // Live clear or update error as user types
                const err = value.trim().length === 0
                  ? t('roles.validation.nameRequired')
                  : (value.trim().length < 2 ? t('roles.validation.nameTooShort') : '')
                setNameError(err)
              }
            }}
              placeholder={t('roles.placeholders.roleName')}
              disabled={loading}
            aria-invalid={!!nameError}
            className={`${nameError ? 'border-destructive' : ''} max-[1149px]:h-9 max-[1149px]:text-sm`}
            />
          {nameError && (
            <p className="mt-1 text-sm text-destructive max-[1149px]:text-xs">{nameError}</p>
          )}
          </div>
          
          <div>
            <label className="text-sm font-medium text-foreground max-[1149px]:text-xs">
              {t('roles.roleColor')}
            </label>
            <div className="flex items-center space-x-2 max-[1149px]:space-x-1.5">
              <div 
                className="w-8 h-8 rounded border-2 border-border flex-shrink-0 max-[1149px]:w-7 max-[1149px]:h-7"
                style={{ backgroundColor: formData.color }}
              />
              <div className="flex space-x-1 max-[1149px]:space-x-0.5 flex-wrap">
                {DEFAULT_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`w-6 h-6 rounded border-2 max-[1149px]:w-5 max-[1149px]:h-5 ${
                      formData.color === color ? 'border-foreground' : 'border-border'
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() => setFormData(prev => ({ ...prev, color }))}
                    disabled={loading}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-foreground max-[1149px]:text-xs">
            {t('roles.roleDescription')}
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            placeholder={t('roles.placeholders.roleDescription')}
            className="w-full p-3 border border-input bg-background text-foreground placeholder-muted-foreground rounded-md resize-none h-20 max-[1149px]:p-2 max-[1149px]:h-16 max-[1149px]:text-sm"
            disabled={loading}
          />
        </div>

        <div className="flex items-center space-x-2 max-[1149px]:space-x-1.5">
          <input
            type="checkbox"
            id="is_active"
            checked={formData.is_active}
            onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
            disabled={loading}
            className="max-[1149px]:w-4 max-[1149px]:h-4"
          />
          <label htmlFor="is_active" className="text-sm text-foreground max-[1149px]:text-xs">
            {t('roles.isActive')}
          </label>
        </div>

        {/* Permissions Selection */}
        <div>
          <div className="flex items-center justify-between mb-4 max-[1149px]:mb-3 max-[1149px]:flex-col max-[1149px]:items-stretch max-[1149px]:gap-2">
            <h3 className="text-lg font-semibold text-foreground max-[1149px]:text-base">
              {t('roles.permissions')} ({formData.selectedPermissions.length})
            </h3>
            {formData.selectedPermissions.length > 0 && (
              <Badge variant="default" className="max-[1149px]:text-xs max-[1149px]:px-1.5">
                {formData.selectedPermissions.length} {t('roles.selected')}
              </Badge>
            )}
          </div>

          {fetchingPermissions ? (
            <div className="flex items-center justify-center py-8 max-[1149px]:py-6">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary max-[1149px]:h-5 max-[1149px]:w-5"></div>
            </div>
          ) : (
            <div className="space-y-4 max-h-96 overflow-y-auto border border-border rounded-lg p-4 max-[1149px]:space-y-3 max-[1149px]:p-3 max-[1149px]:max-h-64">
              {getPermissionCategories().map((category) => {
                const categoryPermissions = getPermissionsByCategory(category)
                const selectedCount = getSelectedCountForCategory(category)
                const allSelected = isAllSelectedForCategory(category)
                const someSelected = isSomeSelectedForCategory(category)

                return (
                  <div key={category} className="border border-border rounded-lg p-4 max-[1149px]:p-3">
                    {/* Category Header */}
                    <div className="flex items-center justify-between mb-3 max-[1149px]:mb-2 max-[1149px]:flex-col max-[1149px]:items-stretch max-[1149px]:gap-2">
                      <div className="flex items-center space-x-2 max-[1149px]:space-x-1.5">
                        <h4 className="font-medium text-foreground max-[1149px]:text-sm truncate flex-1">
                          {t(`roles.permissionCategories.${category}`) || category}
                        </h4>
                        <Badge variant="secondary" className="max-[1149px]:text-xs max-[1149px]:px-1.5 flex-shrink-0">
                          {selectedCount}/{categoryPermissions.length}
                        </Badge>
                      </div>
                      <div className="flex space-x-2 max-[1149px]:w-full max-[1149px]:justify-between">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCategoryToggle(category, true)}
                          disabled={loading || allSelected}
                          className="max-[1149px]:flex-1 max-[1149px]:h-8 max-[1149px]:text-xs"
                        >
                          {t('roles.selectAll')}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCategoryToggle(category, false)}
                          disabled={loading || !someSelected}
                          className="max-[1149px]:flex-1 max-[1149px]:h-8 max-[1149px]:text-xs"
                        >
                          {t('roles.clearAll')}
                        </Button>
                      </div>
                    </div>

                    {/* Permissions Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-[1149px]:grid-cols-1 max-[1149px]:gap-1.5">
                      {categoryPermissions.map((permission) => {
                        const isSelected = formData.selectedPermissions.includes(permission.id)
                        
                        return (
                          <label 
                            key={permission.id}
                            className={`flex items-center space-x-3 p-3 border rounded-lg cursor-pointer transition-colors max-[1149px]:p-2 max-[1149px]:space-x-2 ${
                              isSelected ? 'bg-primary/10 border-primary' : 'hover:bg-muted/50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handlePermissionToggle(permission.id)}
                              disabled={loading}
                              className="rounded max-[1149px]:w-4 max-[1149px]:h-4 flex-shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2 max-[1149px]:space-x-1.5">
                                {isSelected ? (
                                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                                ) : (
                                  <AlertCircle className="h-4 w-4 text-muted-foreground flex-shrink-0 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                                )}
                                <p className="font-medium text-sm text-foreground truncate max-[1149px]:text-xs">
                                  {t(`roles.permissionNames.${permission.codename}`) || permission.name}
                                </p>
                              </div>
                              {permission.description && (
                                <p className="text-xs text-muted-foreground mt-1 max-[1149px]:text-[10px] line-clamp-1">
                                  {t(`roles.permissionDescriptions.${permission.codename}`, permission.description)}
                                </p>
                              )}
                            </div>
                          </label>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
              
              {permissions.length === 0 && (
                <div className="text-center py-8 max-[1149px]:py-6">
          <Info className="h-8 w-8 text-muted-foreground mx-auto mb-2 max-[1149px]:h-6 max-[1149px]:w-6" />
          <p className="text-muted-foreground max-[1149px]:text-sm">{t('roles.noPermissions')}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Selected Permissions Summary */}
        {formData.selectedPermissions.length > 0 && (
          <div className="p-4 bg-primary/5 dark:bg-primary/10 border border-primary/20 dark:border-primary/30 rounded-lg max-[1149px]:p-3">
              <h4 className="font-medium mb-2 text-foreground max-[1149px]:text-sm max-[1149px]:mb-1.5">
                {t('roles.selectedPermissions')} ({formData.selectedPermissions.length})
              </h4>
            <div className="flex flex-wrap gap-2 max-[1149px]:gap-1.5">
              {formData.selectedPermissions.map(permissionId => {
                const permission = permissions.find(p => p.id === permissionId)
                return permission ? (
                  <Badge key={permissionId} variant="secondary" className="text-xs max-[1149px]:text-[10px] max-[1149px]:px-1.5">
                    {t(`roles.permissionNames.${permission.codename}`) || permission.name}
                  </Badge>
                ) : null
              })}
            </div>
          </div>
        )}
      </div>
      
      <div className="flex justify-end space-x-3 mt-6 max-[1149px]:mt-4 max-[1149px]:flex-col max-[1149px]:space-x-0 max-[1149px]:space-y-2">
        <Button 
          variant="outline" 
          onClick={handleClose}
          disabled={loading || fetchingPermissions}
          className="max-[1149px]:w-full max-[1149px]:h-9 max-[1149px]:text-sm"
        >
          {t('users.form.cancel')}
        </Button>
        <Button 
          onClick={handleSubmit}
          disabled={loading || fetchingPermissions}
          className="max-[1149px]:w-full max-[1149px]:h-9 max-[1149px]:text-sm"
        >
          {loading ? t('common.loading') : (isEditMode ? t('roles.updateRole') : t('roles.createRole'))}
        </Button>
      </div>
    </Modal>
  )
}
